import { useMemo } from "react";
import { useGenerateBillOfLadingPdfMutation } from "@api/graphql/generated/generated-types";
import { isSimpleShipment } from "@src/common/lib/shipmentStateFinders";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { useAuth } from "@src/common/hooks/useAuth";
import { useShipmentsCoreDataContext } from "../context/ShipmentsCoreData";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";

interface UseBulkPrintBOLsReturn {
  isDisabled: boolean;
  disabledReason: string | undefined;
  isLoading: boolean;
  handleClick: () => void;
}

/**
 * Bulk-print-BOLs glue for the dispatcher orders-table action bar.
 *
 * Eligibility: every selected row must be a simple 2-stop shipment — same
 * constraint as the per-row "Bill of Lading" dropdown item (gated on
 * `isSimpleShipment`). If any selected row fails the check, the button is
 * disabled and `disabledReason` powers the tooltip — consistent with how
 * `useBulkChangeDrivers` surfaces its eligibility.
 *
 * On click: fires the existing `generateBillOfLadingPdf` mutation with N
 * orderIds. Despite the `[BillOfLadingDoc!]!` return type the server always
 * returns a single-element list at any N — see the schema docstring on
 * `generateBillOfLadingPdf` — so we read `[0].pdfUrl` exactly like the per-row
 * dropdown and portal button. Sharing the mutation across the per-row and
 * bulk-print paths means there's no codegen churn here and no second mutation
 * to keep in sync.
 */
export function useBulkPrintBOLs(): UseBulkPrintBOLsReturn {
  const { rows } = useShipmentsCoreDataContext();
  const { checkedShipments } = useShipmentSelectionContext();
  const { showToast } = useCourieStore();
  const { courierId } = useAuth();

  const [generateBillOfLadingPdfMutation, { loading: isLoading }] =
    useGenerateBillOfLadingPdfMutation();

  const checkedRows = useMemo(
    () => rows.filter((row) => checkedShipments.includes(row?.key ?? "")),
    [rows, checkedShipments]
  );

  const ineligibleCount = useMemo(
    () => checkedRows.filter((row) => !isSimpleShipment(row?.shipment)).length,
    [checkedRows]
  );

  const isDisabled = checkedRows.length === 0 || ineligibleCount > 0;

  const disabledReason: string | undefined =
    ineligibleCount > 0
      ? `${ineligibleCount} of ${checkedRows.length} selected order${
          checkedRows.length === 1 ? "" : "s"
        } cannot have a BOL — only simple 2-stop shipments are supported.`
      : undefined;

  const handleClick = () => {
    if (isDisabled || !courierId) return;
    // `isSimpleShipment` already proved every checked row has a shipment, and
    // `order.id` is non-nullable in the schema — same stance the sibling bulk
    // shipping-label flow takes. If a regression makes that untrue, the
    // resulting TypeError is louder (and easier to triage) than a silent no-op.
    const orderIds: string[] = checkedRows.map(
      (row) => row!.shipment!.order!.id
    );

    generateBillOfLadingPdfMutation({
      variables: { courierId, orderIds },
    })
      .then((res) => {
        const url = res.data?.generateBillOfLadingPdf[0]?.pdfUrl;
        if (url) {
          window.open(url, "_blank");
        } else {
          throw new Error("No PDF URL returned from the server.");
        }
      })
      .catch((error) => {
        showErrorToast(error.message, showToast);
      });
  };

  return {
    isDisabled,
    disabledReason,
    isLoading,
    handleClick,
  };
}

export default useBulkPrintBOLs;
